import { createAsyncThunk, createAction } from "@reduxjs/toolkit";
import adminService from "../../services/adminService";

// ========================================
// ADMIN MANAGEMENT THUNKS
// ========================================

export const getAdminThunk = createAsyncThunk(
  "admin/getAdmin",
  async (id, { rejectWithValue }) => {
    try {
      const response = await adminService.getAdmin(id);
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const getMyDetailsThunk = createAsyncThunk(
  "admin/getMyDetails",
  async (_, { rejectWithValue }) => {
    try {
      const response = await adminService.getMyDetails();
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const createAdminThunk = createAsyncThunk(
  "admin/createAdmin",
  async (adminData, { rejectWithValue }) => {
    try {
      const response = await adminService.createAdmin(adminData);
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const deleteAdminThunk = createAsyncThunk(
  "admin/deleteAdmin",
  async (id, { rejectWithValue }) => {
    try {
      const response = await adminService.deleteAdmin(id);
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const updateAdminPasswordThunk = createAsyncThunk(
  "admin/updateAdminPassword",
  async (passwordData, { rejectWithValue }) => {
    try {
      const response = await adminService.updateAdminPassword(passwordData);
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const resetPasswordThunk = createAsyncThunk(
  "admin/resetPassword",
  async (passwordData, { rejectWithValue }) => {
    try {
      const response = await adminService.resetPassword(passwordData);
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const requestPasswordResetThunk = createAsyncThunk(
  "admin/requestPasswordReset",
  async (data, { rejectWithValue }) => {
    try {
      const response = await adminService.requestPasswordReset(data);
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const fetchAdminsThunk = createAsyncThunk(
  "admin/fetchAdmins",
  async (filter, { rejectWithValue }) => {
    try {
      const response = await adminService.fetchAdmins(filter);
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const toggleBlockAdminThunk = createAsyncThunk(
  "admin/toggleBlockAdmin",
  async ({ adminCode, block }, { rejectWithValue }) => {
    try {
      const response = await adminService.toggleBlockAdmin(adminCode, block);
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const getForgotPasswordRequestsThunk = createAsyncThunk(
  "admin/getForgotPasswordRequests",
  async (_, { rejectWithValue }) => {
    try {
      const response = await adminService.getForgotPasswordRequests();
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const completeInitialSetupThunk = createAsyncThunk(
  "admin/completeInitialSetup",
  async (setupData, { rejectWithValue }) => {
    try {
      const response = await adminService.completeInitialSetup(setupData);
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const updateAdminBranchAccessThunk = createAsyncThunk(
  "admin/updateBranchAccess",
  async ({ adminId, branchData }, { rejectWithValue }) => {
    try {
      const response = await adminService.updateAdminBranchAccess(
        adminId,
        branchData
      );
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

// ========================================
// STATUS MANAGEMENT THUNKS
// ========================================

export const getAdminStatusThunk = createAsyncThunk(
  "admin/getAdminStatus",
  async (adminCode, { rejectWithValue }) => {
    try {
      const response = await adminService.getAdminStatus(adminCode);
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const updateMyStatusThunk = createAsyncThunk(
  "admin/updateMyStatus",
  async (statusData, { rejectWithValue }) => {
    try {
      const response = await adminService.updateMyStatus(statusData);
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const getAvailableAdminsThunk = createAsyncThunk(
  "admin/getAvailableAdmins",
  async (params, { rejectWithValue }) => {
    try {
      const response = await adminService.getAvailableAdmins(params);
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

// ========================================
// AVAILABILITY MANAGEMENT THUNKS
// ========================================

export const getMyAvailabilityThunk = createAsyncThunk(
  "admin/getMyAvailability",
  async (_, { rejectWithValue }) => {
    try {
      const response = await adminService.getMyAvailability();
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const updateWorkingHoursThunk = createAsyncThunk(
  "admin/updateWorkingHours",
  async (workingHoursData, { rejectWithValue }) => {
    try {
      const response = await adminService.updateWorkingHours(workingHoursData);
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const addTimeOffThunk = createAsyncThunk(
  "admin/addTimeOff",
  async (timeOffData, { rejectWithValue }) => {
    try {
      const response = await adminService.addTimeOff(timeOffData);
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const removeTimeOffThunk = createAsyncThunk(
  "admin/removeTimeOff",
  async (timeOffId, { rejectWithValue }) => {
    try {
      const response = await adminService.removeTimeOff(timeOffId);
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const addSpecialHoursThunk = createAsyncThunk(
  "admin/addSpecialHours",
  async (specialHoursData, { rejectWithValue }) => {
    try {
      const response = await adminService.addSpecialHours(specialHoursData);
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

// ========================================
// APPOINTMENT SETTINGS THUNKS
// ========================================

export const updateAppointmentSettingsThunk = createAsyncThunk(
  "admin/updateAppointmentSettings",
  async (settingsData, { rejectWithValue }) => {
    try {
      const response = await adminService.updateAppointmentSettings(
        settingsData
      );
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const toggleAcceptingAppointmentsThunk = createAsyncThunk(
  "admin/toggleAcceptingAppointments",
  async (acceptsAppointments, { rejectWithValue }) => {
    try {
      const response = await adminService.toggleAcceptingAppointments(
        acceptsAppointments
      );
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

// ========================================
// SLOT MANAGEMENT THUNKS
// ========================================

export const getAvailableSlotsThunk = createAsyncThunk(
  "admin/getAvailableSlots",
  async (params, { rejectWithValue }) => {
    try {
      const response = await adminService.getAvailableSlots(params);
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const getAvailableSlotsRangeThunk = createAsyncThunk(
  "admin/getAvailableSlotsRange",
  async (params, { rejectWithValue }) => {
    try {
      const response = await adminService.getAvailableSlotsRange(params);
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

// ========================================
// NOTIFICATION SETTINGS THUNKS
// ========================================

export const updateNotificationSettingsThunk = createAsyncThunk(
  "admin/updateNotificationSettings",
  async (notificationData, { rejectWithValue }) => {
    try {
      const response = await adminService.updateNotificationSettings(
        notificationData
      );
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

// ========================================
// SEARCH & FILTER THUNKS
// ========================================

export const getAdminsBySpecializationThunk = createAsyncThunk(
  "admin/getAdminsBySpecialization",
  async (params, { rejectWithValue }) => {
    try {
      const response = await adminService.getAdminsBySpecialization(params);
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

// ========================================
// INTEGRATION THUNKS
// ========================================

export const getAdminDetailsThunk = createAsyncThunk(
  "admin/getAdminDetails",
  async (adminCode, { rejectWithValue }) => {
    try {
      const response = await adminService.getAdminDetails(adminCode);
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const checkAdminAvailabilityThunk = createAsyncThunk(
  "admin/checkAdminAvailability",
  async ({ adminCode, availabilityData }, { rejectWithValue }) => {
    try {
      const response = await adminService.checkAdminAvailability(
        adminCode,
        availabilityData
      );
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const getBranchAdminsThunk = createAsyncThunk(
  "admin/getBranchAdmins",
  async (branchCode, { rejectWithValue }) => {
    try {
      const response = await adminService.getBranchAdmins(branchCode);
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const getAdminsByRoleThunk = createAsyncThunk(
  "admin/getAdminsByRole",
  async (params, { rejectWithValue }) => {
    try {
      const response = await adminService.getAdminsByRole(params);
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const getBulkAdminAvailabilityThunk = createAsyncThunk(
  "admin/getBulkAdminAvailability",
  async (bulkData, { rejectWithValue }) => {
    try {
      const response = await adminService.getBulkAdminAvailability(bulkData);
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const updateAdminRatingThunk = createAsyncThunk(
  "admin/updateAdminRating",
  async ({ adminCode, ratingData }, { rejectWithValue }) => {
    try {
      const response = await adminService.updateAdminRating(
        adminCode,
        ratingData
      );
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const updateAppointmentStatsThunk = createAsyncThunk(
  "admin/updateAppointmentStats",
  async ({ adminCode, statsData }, { rejectWithValue }) => {
    try {
      const response = await adminService.updateAppointmentStats(
        adminCode,
        statsData
      );
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

// ========================================
// BRANCH ACCESS MANAGEMENT THUNKS
// ========================================

/**
 * Get admin's branch access details
 */
export const getAdminBranchAccessThunk = createAsyncThunk(
  "admin/getAdminBranchAccess",
  async (adminCode, { rejectWithValue }) => {
    try {
      const response = await adminService.getAdminBranchAccess(adminCode);
      return { ...response, adminCode };
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * Add secondary branch access to an admin
 */
export const addBranchAccessThunk = createAsyncThunk(
  "admin/addBranchAccess",
  async ({ adminCode, branchData }, { rejectWithValue }) => {
    try {
      const response = await adminService.addBranchAccess(
        adminCode,
        branchData
      );
      return { ...response, adminCode };
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * Update branch access permissions
 */
export const updateBranchAccessPermissionsThunk = createAsyncThunk(
  "admin/updateBranchAccessPermissions",
  async ({ adminCode, branchCode, updateData }, { rejectWithValue }) => {
    try {
      const response = await adminService.updateBranchAccessPermissions(
        adminCode,
        branchCode,
        updateData
      );
      return { ...response, adminCode, branchCode };
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * Remove secondary branch access from an admin
 */
export const removeBranchAccessThunk = createAsyncThunk(
  "admin/removeBranchAccess",
  async ({ adminCode, branchCode }, { rejectWithValue }) => {
    try {
      const response = await adminService.removeBranchAccess(
        adminCode,
        branchCode
      );
      return { ...response, adminCode, branchCode };
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * Set admin's primary branch
 */
export const setAdminPrimaryBranchThunk = createAsyncThunk(
  "admin/setAdminPrimaryBranch",
  async ({ adminCode, branchCode, accessLevel }, { rejectWithValue }) => {
    try {
      const response = await adminService.setAdminPrimaryBranch(
        adminCode,
        branchCode,
        accessLevel
      );
      return { ...response, adminCode };
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * Get all admins for a specific branch
 */
export const getAdminsByBranchThunk = createAsyncThunk(
  "admin/getAdminsByBranch",
  async ({ branchCode, params }, { rejectWithValue }) => {
    try {
      const response = await adminService.getAdminsByBranch(branchCode, params);
      return { ...response, branchCode };
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * Bulk assign branch access to multiple admins
 */
export const bulkAssignBranchAccessThunk = createAsyncThunk(
  "admin/bulkAssignBranchAccess",
  async ({ branchCode, bulkData }, { rejectWithValue }) => {
    try {
      const response = await adminService.bulkAssignBranchAccess(
        branchCode,
        bulkData
      );
      return { ...response, branchCode };
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

// ========================================
// ACTIONS
// ========================================

export const clearAdminError = createAction("admin/clearAdminError");
export const resetSelectedAdmin = createAction("admin/resetSelectedAdmin");
export const setAdminFilter = createAction("admin/setAdminFilter");
export const clearAvailabilityData = createAction(
  "admin/clearAvailabilityData"
);
export const clearSlotData = createAction("admin/clearSlotData");
export const clearBranchAccessData = createAction(
  "admin/clearBranchAccessData"
);
